// @flow

// Effect Manager
import { fork, put, take, select, call } from 'redux-saga/effects'

// Libs
import _get from 'lodash/get'

// Selectors
import operations$ from '../selectors/operations'
import operation$ from '../selectors/operation'
import { authenticatedUserId$ } from 'fc-authenticated-user'
import { currentUser$ } from 'hfty-users/src'

// Action Types & Constants
import { CREATE_ORDER, UPDATE_USER_BILLING_INFO } from '../constants'
import {
  PROP_ORDER,
  PROP_IS_LOADING,
  PROP_USER_BILLING_INFO,
  PROP_CLIENT,
  PROP_ADDRESS,
  PROP_CITY,
  PROP_COUNTRY,
  PROP_PROVINCE,
  PROP_POSTAL_CODE,
} from 'hfty-model-orders'
import { URL as BILLING_INFO_URL } from 'hfty-model-billing-info'
import { PUT } from 'fc-fetch/src/constants'

// Operator
import ordersOperator from 'hfty-operator-orders'
import orderTestsOperator from 'hfty-operator-order-tests'

// Utils
import { getToken } from 'fc-auth'
import getUrl from 'hfty-utils/url'
import decodeBadResponse from 'fc-decoders/src/badResponse'

// Controllers
import userCtrl from 'fc-ctrl-users'

export default function* watchOrderOperations(): Generator<*, void, *> {
  while (true) {
    const { payload, type } = yield take([CREATE_ORDER, UPDATE_USER_BILLING_INFO])
    if (type === CREATE_ORDER) {
      yield fork(handleCreateOrder, payload)
    } else if (type === UPDATE_USER_BILLING_INFO) {
      yield fork(handleUpdateUserBillingInfo, payload)
    }
  }
}

export function* handleCreateOrder(payload: Object): Generator<*, void, *> {
  const { clientBillingAddress } = yield select(currentUser$)
  const operations = yield select(operations$)
  const action = yield ordersOperator.actionCreators.cancelOperations(operations)
  yield put(action)

  const operation = ordersOperator.create({
    model: {
      [PROP_ORDER]: {
        ...payload[PROP_ORDER],
        [PROP_CLIENT]: yield select(authenticatedUserId$),
      },
      [PROP_USER_BILLING_INFO]: {
        [PROP_ADDRESS]: clientBillingAddress[PROP_ADDRESS],
        [PROP_CITY]: clientBillingAddress[PROP_CITY],
        [PROP_COUNTRY]: clientBillingAddress[PROP_COUNTRY],
        [PROP_PROVINCE]: clientBillingAddress[PROP_PROVINCE],
        [PROP_POSTAL_CODE]: clientBillingAddress[PROP_POSTAL_CODE],
      },
      [PROP_IS_LOADING]: false,
    },
  })

  yield put(ordersOperator.actionCreators.add(operation))
}

export function* handleUpdateUserBillingInfo(payload: Object): Generator<*, void, *> {
  const token = yield call(getToken)
  const operation = yield select(operation$)
  const user = yield select(currentUser$)
  const { orderTestOperation } = payload
  const id = _get(user, 'clientBillingAddress.id')
  const operator = orderTestOperation ? orderTestsOperator : ordersOperator
  const clientBillingAddress = {
    ...user.clientBillingAddress,
    [PROP_ADDRESS]: payload[PROP_ADDRESS],
    [PROP_CITY]: payload[PROP_CITY],
    [PROP_COUNTRY]: payload[PROP_COUNTRY],
    [PROP_PROVINCE]: payload[PROP_PROVINCE],
    [PROP_POSTAL_CODE]: payload[PROP_POSTAL_CODE],
  }

  const response = yield call(
    fetch,
    getUrl({
      path: BILLING_INFO_URL,
      id,
    }),
    {
      method: PUT,
      body: JSON.stringify(clientBillingAddress),
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'application/json',
      },
    }
  )

  if (response.status >= 200 && response.status < 300) {
    yield put(
      userCtrl.actionCreators.receive({
        id,
        data: {
          [id]: {
            ...user,
            clientBillingAddress,
          },
        },
      })
    )

    yield put(
      !orderTestOperation
        ? operator.actionCreators.submitOperations()
        : operator.actionCreators.submitOperation(orderTestOperation)
    )
  } else {
    const json = yield call([response, response.json])
    const nextOperation = {
      ...operation,
      submitting: false,
      success: false,
      errors: decodeBadResponse(json),
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)
  }
}
